import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import { ContentLanguageHeaderName } from "@/api/ApiClientProvider";
import { useApi } from "@/api/hooks/useApi";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import type { ApiQueryParams } from "@/types/api-types";
import type { EntityDetailsQueryOptions } from "@/types/query-types";

type SlotsQuery = ApiQueryParams<"getBookableAssetsSlotsV1", 1>;
type AssetBookingsQuery = ApiQueryParams<"getBookableAssetsBookingsV1", 1>;

export const useBookableAssetsQueries = () => {
  const projectId = useProjectId();
  const api = useApi();

  return {
    list: ({ query }: { query: ApiQueryParams<"getBookableAssetsV1"> }) =>
      queryOptions({
        queryKey: QUERY_KEYS.BOOKINGS_ASSETS(projectId, query),
        queryFn: () => api.getBookableAssetsV1(query),
        select: (data) => commonAPIDataSelector(data).items,
      }),
    listInfinite: ({ query }: { query: ApiQueryParams<"getBookableAssetsV1"> }) => {
      const limit = 12;

      return infiniteQueryOptions({
        queryKey: QUERY_KEYS.BOOKINGS_ASSETS_INFINITE(projectId, query),
        queryFn: ({ pageParam = 0 }) =>
          api
            .getBookableAssetsV1({
              ...query,
              Offset: pageParam * limit,
              Limit: limit,
            })
            .then((items) => commonAPIDataSelector(items)),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => (lastPage?.hasMore ? pages?.length : undefined),
      });
    },
    listMyBookingsInfinite: ({ query }: { query: ApiQueryParams<"getBookableAssetsMyBookingsV1"> }) => {
      const limit = 8;

      return infiniteQueryOptions({
        queryKey: QUERY_KEYS.BOOKINGS_USER_RESERVATIONS(projectId, query),
        queryFn: ({ pageParam = 0 }) =>
          api
            .getBookableAssetsMyBookingsV1({
              ...query,
              Offset: pageParam * limit,
              Limit: pageParam === 0 ? limit : undefined,
            })
            .then((items) => commonAPIDataSelector(items)),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => (lastPage?.hasMore ? pages?.length : undefined),
      });
    },
    details: ({ assetId }: { assetId: string }, options?: EntityDetailsQueryOptions) => {
      const headers = {
        [ContentLanguageHeaderName]: options?.translationMode || "default",
      };

      return queryOptions({
        queryKey: QUERY_KEYS.BOOKINGS_ASSET_DETAILS(projectId, assetId, options),
        queryFn: () =>
          api.getBookableAssetsDetailsV1(assetId, {
            headers,
          }),
        select: commonAPIDataSelector,
      });
    },
    statistics: ({ assetId }: { assetId: string }) => {
      return queryOptions({
        queryKey: QUERY_KEYS.BOOKINGS_ASSET_STATISTICS(projectId, assetId),
        queryFn: () => api.getBookableAssetsStatisticsV1(assetId, { includeToday: true }),
        select: commonAPIDataSelector,
      });
    },
    slots: ({ assetId, query }: { assetId: string; query?: SlotsQuery }) =>
      queryOptions({
        queryKey: QUERY_KEYS.BOOKINGS_ASSET_SLOTS(projectId, assetId, query),
        queryFn: () => api.getBookableAssetsSlotsV1(assetId, query),
        select: commonAPIDataSelector,
      }),
    bookingDetails: ({ assetId, bookingId }: { assetId: string; bookingId: string }) =>
      queryOptions({
        queryKey: QUERY_KEYS.BOOKINGS_RESERVATION_DETAILS(projectId, assetId, bookingId),
        queryFn: () => api.getBookableAssetsBookingsDetailsV1(bookingId, assetId),
        select: commonAPIDataSelector,
      }),
    assetBookings: ({ assetId, query }: { assetId: string; query?: AssetBookingsQuery }) =>
      queryOptions({
        queryKey: QUERY_KEYS.BOOKINGS_ASSET_BOOKINGS(projectId, assetId, query),
        queryFn: () => api.getBookableAssetsBookingsV1(assetId, query),
        select: commonAPIDataSelector,
      }),
  };
};
